import { successToast } from 'Components/Notifications';
import { useCurrentPopup } from 'Components/Popups';
import { useConfirmDeleteModal } from 'Components/Popups/ConfirmDeletePopup';
import {
  IDirectoryNode,
  useCopyFileObjectMutation,
  useDeleteNodeMutation,
  useMoveToTrashMutation,
} from 'app/services/documents';
import { useDocumentsContext } from '.';
import { useHavePermissions } from './DirectoryNodeDetails';

export function downloadFile(url: string, filename: string) {
  fetch(url)
    .then((response) => response.blob())
    .then((blob) => {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
    })
    .catch(console.error);
}

export default function useActions() {
  const { close, contextData } = useCurrentPopup<IDirectoryNode>();
  const [DeleteTrigger] = useDeleteNodeMutation();
  const [CopyFileObject] = useCopyFileObjectMutation();
  const [MoveToTrashTrigger] = useMoveToTrashMutation();

  const confirmDelete = useConfirmDeleteModal();
  const {
    updateQueryParamValue,
    CreateNodeModal,
    DocumentDetailsModal,
    refetchAfterDelete,
    refetchAfterAdd,
    FolderDetailsModal,
    ShareModal,
    MoveModal,
    RestoreModal,
    selectItem,
    unselectItem,
    per_page,
    directory: gridDirectory,
  } = useDocumentsContext();
  const node = contextData as IDirectoryNode;

  const deleteNode = () => {
    confirmDelete(node.object_type, () =>
      DeleteTrigger(node.id)
        .unwrap()
        .then((_) => {
          node.id === gridDirectory.id // deleting parent directory
            ? updateQueryParamValue('id', gridDirectory.parent_id || undefined)
            : refetchAfterDelete();
          unselectItem(node);
        })
        .then(close),
    );
  };
  const viewDetails = () => {
    close();
    node.object_type === 'folder'
      ? FolderDetailsModal.openWithContext({ data: node })
      : DocumentDetailsModal.openWithContext({ data: node });
  };
  const createNode = (object_type: IDirectoryNode['object_type']) => () => {
    if (node.object_type === 'folder') {
      close();
      CreateNodeModal.openWithContext({
        data: {
          parent: node,
        },
      });
    }
  };

  const share = () => {
    close();
    ShareModal.openWithContext({ data: node });
  };

  const move = () => {
    close();
    selectItem(node);
    MoveModal.openWithContext({ data: [node] });
  };

  const restore = () => {
    close();
    selectItem(node);
    RestoreModal.openWithContext({ data: [node] });
  };

  const downloadDocument = () => {
    if (node.object_type === 'document') {
      const url = process.env.REACT_APP_URL + node.file_url;
      downloadFile(url, node.title);
      close();
    }
  };

  const copyFolder = () => {
    CopyFileObject({
      destination_parent_id: node.parent_id || null,
      id: node.id,
    })
      .unwrap()
      .then(() => {
        successToast('Folder copied');
        close();
        refetchAfterAdd(gridDirectory?.subfolders_count, per_page);
      });
  };

  const moveToTrash = () => {
    MoveToTrashTrigger({
      destination_parent_id: null,
      id: node.id,
    })
      .unwrap()
      .then(() => {
        successToast('Moved to trash');
        close();
        refetchAfterDelete();
      });
  };

  const isOwner = useHavePermissions()(node);
  const canRestore = isOwner && node.is_trashed;
  const canDelete = isOwner && !!node.is_trashed;
  const canMove = isOwner && !node.is_trashed;
  const canShare = isOwner && !node.is_trashed;
  const canMoveToTrash = isOwner && !node.is_trashed;
  const canAdd = isOwner && !node.is_trashed;
  const canCopy = isOwner && !node.is_trashed;

  return {
    deleteNode,
    share,
    move,
    moveToTrash,
    restore,
    downloadDocument,
    createNode,
    viewDetails,
    copyFolder,
    type: node.object_type,
    node,
    isOwner,
    canMoveToTrash,
    canRestore,
    canDelete,
    canMove,
    canShare,
    canAdd,
    canCopy,
  };
}
